import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import { getBopProfitAndLoss } from 'slices/bopSlice'
import { getBopMonitoringSummary, getTenantSummary } from 'slices/dashboardSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { getWorkspaceList } from 'slices/workspacesSlice'

import { PivotItem, PivotOuterIndex } from 'components/common'

import useBusinessTime from 'hooks/useBusinessTime'
import useDateChange from 'hooks/useDateChange'
import useDateQuery from 'hooks/useDateQuery'
import { useQuery } from 'hooks/useQuery'

import BopMonitoringProfitAndLoss from './BopMonitoringProfitAndLoss'
import { BopMonitoringSummary } from './BopMonitoringSummary'
import Dashboard from './Dashboard'
import { BOP_TAB_TYPES, type BOPTabType } from './utils'

export const BopMonitoring = () => {
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<number[]>([])
  const [selectedKeyScheduleTypeIds, setSelectedKeyScheduleTypeIds] = useState<number[]>([])

  const { user } = useSelector(selectSessionStatus, shallowEqual)
  const dispatch = useDispatch()
  const { getWorkDate } = useBusinessTime()
  const [, setSearchParams] = useSearchParams()

  const date = getWorkDate(useDateQuery())
  const { checkTenantCreated } = useDateChange()
  const [queryTab] = useQuery('tab', BOP_TAB_TYPES.SUMMARY)

  const tabs = useMemo(
    () => [
      {
        header: 'サマリー',
        key: BOP_TAB_TYPES.SUMMARY,
        component: (
          <BopMonitoringSummary
            onSelectedWorkspacesChange={setSelectedWorkspaceIds}
            onSelectedKeyScheduleTypesChange={setSelectedKeyScheduleTypeIds}
          />
        ),
      },
      {
        header: '損益詳細',
        key: BOP_TAB_TYPES.PROFIT_AND_LOSS_DETAIL,
        component: <BopMonitoringProfitAndLoss />,
      },
    ],
    []
  )

  // ダッシュボード収支モニタリング取得
  useEffect(() => {
    dispatch(getBopProfitAndLoss(date))
    dispatch(getTenantSummary(date, { dashboardFilter: true }))
  }, [dispatch, date])

  useEffect(() => {
    dispatch(getWorkspaceList({ workDate: date, tenantId: user.userHasTenants[0].id }))
  }, [dispatch, user, date])

  const handleInterval = useCallback(() => {
    const workspacesParam = selectedWorkspaceIds.length === 0 ? {} : { workspaceIds: selectedWorkspaceIds.join() }
    const keyScheduleTypesParam =
      selectedKeyScheduleTypeIds.length === 0 ? {} : { scheduleTypeIds: selectedKeyScheduleTypeIds.join() }
    dispatch(getBopProfitAndLoss(date))
    dispatch(getBopMonitoringSummary(date, { ...workspacesParam, ...keyScheduleTypesParam }))
  }, [dispatch, date, selectedWorkspaceIds, selectedKeyScheduleTypeIds])

  const handleDateChange = useCallback((newDate: string) => checkTenantCreated(newDate), [checkTenantCreated])

  const handleTabChange = useCallback(
    (key: BOPTabType) => {
      setSearchParams(
        params => {
          params.set('tab', key)
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return (
    <Dashboard onInterval={handleInterval} onDateChange={handleDateChange}>
      <PivotOuterIndex
        selectedIndex={tabs.findIndex(tab => tab.key === queryTab)}
        onChange={index => handleTabChange(tabs[index].key)}
      >
        {tabs.map(({ header, key, component }) => (
          <PivotItem headerText={header} key={key}>
            {component}
          </PivotItem>
        ))}
      </PivotOuterIndex>
    </Dashboard>
  )
}
